import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ToastrNotificationService } from '../ui/toastr-notification.service';
import {
  ApiData,
  ApiMessage,
  ApiResponse,
} from '../../interfaces/api/api-response.interface';
import { Observable, tap } from 'rxjs';
import { Message } from '../../interfaces/api/message.interfaces';
import { EncryptionService } from '../ui/encryption.service';
import { DEFAULT_SEARCH_NG_SELECT_PAGINATION } from '../../helpers/ui/ui.constant';
import { ChatMessage, PaginatedResponse } from '../../interfaces/ui/message-chat.interface';

@Injectable({
  providedIn: 'root',
})
export class MessageService {
  private apiUrl: string = environment.apiUrl;
  private path: string = '/message';

  constructor(
    private _httpClient: HttpClient,
    private _notificationService: ToastrNotificationService,
    private _encryptionService: EncryptionService
  ) {}

  getAllUnreadMessages(filter?: {}): Observable<
    ApiResponse<ApiData<Message[]>>
  > {
    const endpoint = `${this.apiUrl}${this.path}/get-all-unread`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<Message[]>>>(endpoint, {
      params,
    });
  }

  getMessageInbox(
    entityId: string,
    fromId: string,
    filter?: {}
  ): Observable<ApiResponse<ApiData<Message[]>>> {
    const data = this._encryptionService.encrypt(
      JSON.stringify({
        entityId,
        fromId,
        entityType: 'User',
        fromType: 'Player',
        ...filter,
      })
    );

    let params = new HttpParams().set('data', data.toString());

    return this._httpClient.get<ApiResponse<ApiData<Message[]>>>(
      `${this.apiUrl}${this.path}/inbox`,
      {
        params,
      }
    );
  }

  getMessageSent(
    entityId: string,
    toId: string,
    filter?: {}
  ): Observable<ApiResponse<ApiData<Message[]>>> {
    const data = this._encryptionService.encrypt(
      JSON.stringify({
        entityId,
        toId,
        entityType: 'User',
        toType: 'Player',
        ...filter,
      })
    );

    let params = new HttpParams().set('data', data.toString());

    return this._httpClient.get<ApiResponse<ApiData<Message[]>>>(
      `${this.apiUrl}${this.path}/sent`,
      {
        params,
      }
    );
  }

  createMessage(request: object): Observable<ApiResponse<ApiData<Message[]>>> {
    return this._httpClient
      .post<ApiResponse<ApiData<Message[]>>>(
        `${this.apiUrl}${this.path}/create`,
        request
      )
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  getInboxMessagesByChats(
    page: number = DEFAULT_SEARCH_NG_SELECT_PAGINATION.PAGE,
    limit: number = DEFAULT_SEARCH_NG_SELECT_PAGINATION.LIMIT,
    search?: string,
    state?: number,
  ): Observable<ApiResponse<PaginatedResponse<ChatMessage>>> {
    const endpoint = `${this.apiUrl}${this.path}/chats`;
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (state !== undefined) {
      params = params.set('state', state.toString());
    }

    return this._httpClient.get<ApiResponse<PaginatedResponse<ChatMessage>>>(endpoint, { params });
  }

  getChatMessages(participantId: string, page: number = 1, limit: number = 10): Observable<ApiResponse<any>> {
    const endpoint = `${this.apiUrl}${this.path}/chats/${participantId}`;
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this._httpClient.get<ApiResponse<any>>(endpoint, { params });
  }

  setMessageStatus(messageId: string, state: number): Observable<ApiResponse<any>> {
    const endpoint = `${this.apiUrl}${this.path}/set-status`;
    return this._httpClient.put<ApiResponse<any>>(endpoint, { messageId, state });
  }

  updateStatusRead(messageId: string, isRead: boolean): Observable<ApiResponse<any>> {
    const endpoint = `${this.apiUrl}${this.path}/update-status-read`;
    return this._httpClient.patch<ApiResponse<any>>(endpoint, { messageId, isRead });
  }

  private showNotification(message: ApiMessage): void {
    this._notificationService.showNotification({
      title: 'players.messages.message',
      message: message,
      type: 'success',
    });
  }
}
